import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Paper, 
    TextField, 
    Button, 
    Typography,
    Box,
    Alert,
    Snackbar,
    CircularProgress,
    Collapse,
    InputAdornment,
    IconButton,
    Tooltip
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { login, getAuthProvider, azureAuthLogin, getShowMultiTenantClients, getPostLoginPath } from '../../services/auth';
import { CONFIG } from '../../config/config';

const Login = () => {
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [clientId, setClientId] = useState('');  // Optional client/tenant identifier for multi-tenant support
    const [licenseKey, setLicenseKey] = useState('');  // License key for client validation
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
    const [authProvider, setAuthProvider] = useState<string | null>(null);
    const [authProviderLoading, setAuthProviderLoading] = useState(true);
    const [showMultiTenantClients, setShowMultiTenantClients] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showLicenseKey, setShowLicenseKey] = useState(false);
    const [licenseValid, setLicenseValid] = useState<boolean | null>(null);
    const [licenseValidating, setLicenseValidating] = useState(false);

    const fetchAuthProvider = async () => {
        try {
            setAuthProviderLoading(true);
            const [provider, multiTenantFlag] = await Promise.all([
                getAuthProvider(),
                getShowMultiTenantClients()
            ]);
            setAuthProvider(provider);
            setShowMultiTenantClients(multiTenantFlag);
        } catch (err) {
            console.error('Failed to fetch auth provider:', err);
            setError('Failed to load authentication configuration');
            // Default to 'default' auth if API call fails
            setAuthProvider('default');
            setShowMultiTenantClients(false);
        } finally {
            setAuthProviderLoading(false);
        }
    };

    useEffect(() => {
        fetchAuthProvider();
    }, []);

    const validateClient = async (showError: boolean = true): Promise<boolean> => {
        // If no client ID, use default - no validation needed
        if (!clientId.trim()) {
            setLicenseValid(null);
            return true;
        }
        
        // If client ID is provided, license key is required
        if (!licenseKey.trim()) {
            if (showError) {
                setError('License key is required for non-default organizations');
            }
            setLicenseValid(null);
            return false;
        }
        
        try {
            setLicenseValidating(true);
            const response = await fetch(`${CONFIG.SERVER_URL}/validate_client`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ client_id: clientId, license_key: licenseKey })
            });
            
            const data = await response.json();
            
            if (!data.is_valid) {
                if (showError) {
                    setError(data.error_message || 'Organization validation failed');
                }
                setLicenseValid(false);
                return false;
            }
            setLicenseValid(true);
            setError('');
            return true;
        } catch (err: any) {
            console.error('Client validation error:', err);
            if (showError) {
                setError('Failed to validate organization. Please try again.');
            }
            setLicenseValid(false);
            return false;
        } finally {
            setLicenseValidating(false);
        }
    };

    const handleLicenseKeyBlur = () => {
        if (clientId.trim() && licenseKey.trim()) {
            validateClient(false);
        }
    };

    const handleManualValidate = () => {
        if (clientId.trim() && licenseKey.trim()) {
            validateClient(true);
        } else if (clientId.trim() && !licenseKey.trim()) {
            setError('Please enter a license key to validate');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // First validate client if provided
            const isClientValid = await validateClient();
            if (!isClientValid) {
                setLoading(false);
                return;
            }
            
            // Then perform login - pass license_key for server-side validation
            await login(userName, password, clientId || undefined, licenseKey || undefined);
            navigate(getPostLoginPath());
        } catch (err: any) {
            console.error('Login error:', err);
            setError(err.message || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = () => {
        setForgotPasswordOpen(true);
    };

    const handleAzureLogin = async () => {
        setError('');
        setLoading(true);
        
        try {
            await azureAuthLogin();
            navigate(getPostLoginPath());
        } catch (err: any) {
            console.error('Azure login error:', err);
            setError(err.message || 'Azure AD login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: 'background.default',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
        }}>
            <Container maxWidth="sm">
                <Paper 
                    elevation={2} 
                    sx={{ 
                        p: 6,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}
                >
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography 
                            variant="h3" 
                            sx={{ 
                                fontWeight: 700,
                                mb: 1,
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text'
                            }}
                        >
                            Welcome Back
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Sign in to your Statusense account
                        </Typography>
                    </Box>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    {authProviderLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography variant="body2" sx={{ ml: 2 }}>
                                Loading authentication...
                            </Typography>
                        </Box>
                    ) : authProvider === 'default' ? (
                        <form onSubmit={handleSubmit}>
                            {showMultiTenantClients && (
                                <>
                                    <TextField
                                        fullWidth
                                        label="Client ID"
                                        margin="normal"
                                        value={clientId}
                                        onChange={(e) => setClientId(e.target.value)}
                                        placeholder="Leave empty for default"
                                        helperText="Enter your organization's client ID or leave blank for the default org"
                                        size="small"
                                    />
                                    <Collapse in={!!clientId.trim()}>
                                        <TextField
                                            fullWidth
                                            label="License Key"
                                            margin="normal"
                                            value={licenseKey}
                                            onChange={(e) => {
                                                setLicenseKey(e.target.value);
                                                setLicenseValid(null);
                                            }}
                                            onBlur={handleLicenseKeyBlur}
                                            placeholder="Enter your license key"
                                            helperText="Required for non-default organizations"
                                            size="small"
                                            type={showLicenseKey ? 'text' : 'password'}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => setShowLicenseKey(!showLicenseKey)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showLicenseKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                        </IconButton>
                                                        <Tooltip title="Validate license">
                                                            <IconButton
                                                                onClick={handleManualValidate}
                                                                edge="end"
                                                                size="small"
                                                                disabled={licenseValidating}
                                                            >
                                                                {licenseValidating ? (
                                                                    <CircularProgress size={18} />
                                                                ) : licenseValid === true ? (
                                                                    <CheckCircle fontSize="small" color="success" />
                                                                ) : (
                                                                    <Refresh fontSize="small" color={licenseValid === false ? 'error' : 'action'} />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Collapse>
                                </>
                            )}
                            <TextField
                                fullWidth
                                label="Username"
                                margin="normal"
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                                required
                            />
                            <TextField
                                fullWidth
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                margin="normal"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    sx={{ px: 4, py: 1 }}
                                    disabled={loading}
                                >
                                    {loading ? 'Signing in...' : 'Login'}
                                </Button>
                                <Typography 
                                    variant="body2" 
                                    color="primary" 
                                    onClick={handleForgotPassword}
                                    sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    Forgot Password?
                                </Typography>
                            </Box>
                        </form>
                    ) : authProvider === 'azure' ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                Sign in with your Microsoft account
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAzureLogin}
                                disabled={loading}
                                sx={{ px: 4, py: 1.5 }}
                            >
                                {loading ? 'Signing in...' : 'Sign in with Microsoft'}
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                Unknown authentication provider: {authProvider}
                            </Typography>
                        </Box>
                    )}
                </Paper>
                <Snackbar
                    open={forgotPasswordOpen}
                    autoHideDuration={6000}
                    onClose={() => setForgotPasswordOpen(false)}
                    message="Password reset functionality coming soon!"
                />
            </Container>
        </Box>
    );
};

export default Login;